<template>
  <div class="loading-container">
    <!-- Exibe erros -->
    <ErrorAlert :errors="errors" />

    <!-- Loading Spinner -->
    <div v-if="loading" class="loading-overlay" role="status">
      <div class="loading-card">
        <span class="spinner"></span>
        <span class="label">{{ progressLabel || $t("Carregando") }}</span>
        <v-btn v-if="currentJobId" text small @click="cancelPlanning"
          >Cancelar planejamento</v-btn
        >
      </div>
    </div>

    <v-container fluid :class="{ 'pa-0': embedded }">
      <div v-if="!embedded" class="d-flex justify-end mb-2">
        <v-btn
          :to="{ name: 'RoteirizadorParametro' }"
          :disabled="loading"
          class="
            ma-1
            font-weight-bold
            text-none
            btn-ls btn-secondary
            bg-gradient-light
            px-6
          "
          height="39"
        >
          <v-icon left>mdi-cog</v-icon>Configurar roteirizador
        </v-btn>
      </div>
      <!-- Seleção de data e arquivo CSV -->
      <v-row v-if="!embedded" class="mb-4">
        <v-col cols="12" md="2">
          <CampoData
            id="dataInicial"
            :payload="selectedDate"
            label="Data"
            @date="handleDataChange"
            :disabled="loading"
          ></CampoData>
        </v-col>
        <v-col cols="12" md="4">
          <v-file-input
            v-model="file"
            label="Arquivo CSV"
            hint="A data selecionada será aplicada a todos os pedidos do arquivo."
            persistent-hint
            accept=".csv"
            outlined
          ></v-file-input>
        </v-col>
        <v-col cols="12" md="4" class="d-flex align-center">
          <!-- Botão que chama importCSV() -->
          <v-btn
            color="primary"
            @click="importCSV"
            :loading="loading"
            class="
              ma-1
              font-weight-bold
              text-none
              btn-primary
              bg-gradient-primary
              py-3
              px-6
              ms-3
            "
          >
            {{ $t("Processar") }}
          </v-btn>
          <!-- Exportar resultado para Excel -->
          <v-btn
            color="primary"
            @click="exportToExcel"
            :loading="loading"
            class="
              ma-1
              font-weight-bold
              text-none
              btn-primary
              bg-gradient-primary
              py-3
              px-6
              ms-3
            "
          >
            {{ $t("Download") }}
          </v-btn>
        </v-col>
      </v-row>

      <v-alert
        v-if="currentJobId && !loading"
        color="#175cd3"
        icon="mdi-information-outline"
        text
      >
        O planejamento continua disponível para acompanhamento.
        <v-btn text @click="resumePlanning">Retomar acompanhamento</v-btn>
      </v-alert>
      <v-row v-if="resumoRotas.length && !embedded">
        <v-col cols="12">
          <v-alert color="#175cd3" icon="mdi-information-outline" text>
            {{ totalRotas }} veículos · {{ rotas.length }} pedidos atendidos ·
            {{
              resumoRotas
                .reduce((sum, item) => sum + (item.totalKm || 0), 0)
                .toFixed(1)
            }}
            km
          </v-alert>
        </v-col>
      </v-row>
      <!-- Rotas ao lado do mapa -->
      <v-row v-if="rotas.length" class="plan-result">
        <v-col cols="12" md="4" class="plan-result__side">
          <section class="plan-panel">
            <header class="plan-panel__header">
              <span class="plan-panel__title">
                <v-icon small color="#5e72e4" class="mr-1">mdi-routes</v-icon>
                <template v-if="selectedRotaNumber">
                  Atendimentos da rota {{ selectedRotaNumber }} ({{
                    selectedRotas.length
                  }})
                </template>
                <template v-else>Rotas ({{ totalRotas }})</template>
              </span>
              <v-btn
                v-if="selectedRotaNumber"
                small
                text
                class="text-none"
                @click="clearRouteSelection"
              >
                <v-icon left small>mdi-arrow-left</v-icon>
                Todas as rotas
              </v-btn>
            </header>
            <div class="plan-panel__body">
              <template v-if="!selectedRotaNumber">
                <button
                  v-for="rotaNumber in uniqueRotaNumbers"
                  :key="rotaNumber"
                  type="button"
                  class="plan-route"
                  @click="selectRota(rotaNumber)"
                >
                  <span
                    class="plan-route__color"
                    :style="{ backgroundColor: getLegendColor(rotaNumber) }"
                  ></span>
                  <span class="plan-route__text">
                    <strong
                      >Rota {{ rotaNumber }} ·
                      {{ getPlaca(rotaNumber) }}</strong
                    >
                    <small>{{ getRouteType(rotaNumber) }}</small>
                  </span>
                  <v-icon small color="#98a2b3">mdi-chevron-right</v-icon>
                </button>
              </template>
              <template v-else-if="selectedStops.length">
                <div
                  v-for="stop in selectedStops"
                  :key="`${stop.numeroRota}-${stop.sequencia}`"
                  class="plan-stop"
                >
                  <span
                    class="plan-stop__sequence"
                    :style="{
                      borderColor: getLegendColor(selectedRotaNumber),
                    }"
                    >{{ stop.sequencia + 1 }}</span
                  >
                  <div class="plan-stop__content">
                    <strong>{{ stop.descricao }}</strong>
                    <span class="plan-stop__type">{{
                      stopLabel(stop.tipo)
                    }}</span>
                    <span v-if="stop.pedido">Pedido {{ stop.pedido }}</span>
                    <span>
                      {{ formatStopTime(stop.inicioAtendimento) }} até
                      {{ formatStopTime(stop.fimAtendimento) }}
                    </span>
                    <span
                      v-if="stop.atrasoSegundos > 0"
                      class="plan-stop__warning"
                    >
                      Início com {{ Math.ceil(stop.atrasoSegundos / 60) }} min
                      de atraso permitido
                    </span>
                    <span>
                      Carga após operação:
                      {{ formatCurrency(stop.cargaDepoisCentavos / 100) }}
                    </span>
                    <span v-if="stop.sequencia + 1 < selectedStops.length">
                      Próximo trecho:
                      {{
                        stop.proximoTrechoInterurbano ? "Interurbano" : "Urbano"
                      }}
                      · limite
                      {{ formatCurrency(stop.limiteTrechoCentavos / 100) }}
                    </span>
                  </div>
                </div>
              </template>
              <template v-else>
                <button
                  v-for="rota in selectedRotas"
                  :key="rota.pedido"
                  type="button"
                  class="plan-stop plan-stop--button"
                  :class="{
                    'selected-route':
                      selectedDetail && selectedDetail.pedido === rota.pedido,
                  }"
                  @click="selectDetail(rota)"
                >
                  <div class="plan-stop__content">
                    <strong>
                      {{ rota.descricaoOrigem }} → {{ rota.descricaoDestino }}
                    </strong>
                    <span>Pedido {{ rota.pedido }}</span>
                    <span v-if="rota.rotaMunicipal !== undefined">
                      Rota municipal: {{ rota.rotaMunicipal ? "Sim" : "Não" }}
                    </span>
                    <span v-if="rota.valor">
                      Valor: {{ formatCurrency(rota.valor) }}
                    </span>
                    <span v-if="rota.previsaoAtendimentoOrigem">
                      Previsão na origem: {{ rota.previsaoAtendimentoOrigem }}
                    </span>
                    <span v-if="rota.previsaoAtendimentoDestino">
                      Previsão no destino: {{ rota.previsaoAtendimentoDestino }}
                    </span>
                    <span v-if="rota.montanteAtual !== undefined">
                      Montante atual: {{ formatCurrency(rota.montanteAtual) }}
                    </span>
                  </div>
                </button>
              </template>
            </div>
          </section>
        </v-col>

        <v-col cols="12" md="8">
          <section class="plan-panel">
            <header class="plan-panel__header">
              <span class="plan-panel__title">
                <v-icon small color="#5e72e4" class="mr-1"
                  >mdi-map-outline</v-icon
                >
                Mapa
              </span>
              <v-btn
                v-if="selectedDetail"
                small
                text
                class="text-none"
                @click="clearSelection"
              >
                <v-icon left small>mdi-close</v-icon>
                {{ $t("Limpar") }}
              </v-btn>
            </header>
            <div id="map" class="plan-map"></div>
            <div v-if="polylines.length" class="plan-panel__footnote">
              Trajetos: ©
              <a
                href="https://www.openstreetmap.org/copyright"
                target="_blank"
                rel="noopener noreferrer"
                >OpenStreetMap contributors</a
              >
            </div>
          </section>
        </v-col>
      </v-row>

      <!-- Lista de atendimentos inviáveis -->
      <v-row v-if="atendimentosInviaveis.length">
        <v-col cols="12">
          <section class="plan-panel">
            <header class="plan-panel__header plan-panel__header--wrap">
              <span class="plan-panel__title">
                <v-icon small color="#b54708" class="mr-1">
                  mdi-alert-outline
                </v-icon>
                Pedidos não alocados ({{ atendimentosInviaveis.length }})
              </span>
              <div class="d-flex flex-wrap">
                <span
                  v-for="(item, i) in resumoMotivos"
                  :key="i"
                  class="plan-reason"
                >
                  {{ item.motivo }} ({{ item.count }})
                </span>
              </div>
            </header>
            <div class="plan-panel__body plan-panel__body--unallocated">
              <div
                v-for="(atendimento, index) in atendimentosInviaveis"
                :key="index"
                class="plan-stop"
              >
                <div class="plan-stop__content">
                  <strong>
                    Pedido {{ atendimento.pedido }} ·
                    {{ atendimento.descricaoOrigem }} →
                    {{ atendimento.descricaoDestino }}
                  </strong>
                  <span>
                    {{ getMotivo(atendimento) || "Motivo não informado" }}
                  </span>
                </div>
              </div>
            </div>
          </section>
        </v-col>
      </v-row>
    </v-container>
  </div>
</template>

<script>
import { MarkerClusterer } from "@googlemaps/markerclusterer";
import DOMPurify from "dompurify";
import { parseRoteirizacaoCsv } from "@/utils/roteirizacaoCsv";
import DataService from "@/services/ComumDataService";
import {
  createGoogleMapsMarker,
  openGoogleMapsInfoWindow,
  withGoogleMapsMapId,
} from "@/utils/googleMapsMarkers";
import * as XLSX from "xlsx";

export default {
  name: "PlanRoutes",
  props: {
    embedded: { type: Boolean, default: false },
    resultado: { type: Object, default: null },
  },
  watch: {
    resultado: {
      immediate: true,
      handler(result) {
        if (!result) return;
        this.applyPlanningResult(result);
        this.$nextTick(async () => {
          try {
            await this.loadGoogleMaps();
            if (!this.disposed) this.updateMap();
          } catch (error) {
            this.errors = [
              {
                message:
                  "Não foi possível carregar o mapa. Os atendimentos continuam disponíveis na lista.",
              },
            ];
          }
        });
      },
    },
  },
  data() {
    return {
      selectedDate: "",
      file: null,
      loading: false,
      errors: [],
      rotas: [],
      paradas: [],
      resumoRotas: [],
      currentJobId: null,
      progressLabel: "",
      disposed: false,
      atendimentosInviaveis: [],
      selectedRotaNumber: null,
      selectedRotas: [],
      selectedDetail: null,
      map: null,
      markers: [],
      polylines: [],
      markerClusterer: null,
      colorPalette: [
        "#1F77B4",
        "#0A9396",
        "#2CA02C",
        "#F4A261",
        "#8C6D31",
        "#7F7F7F",
        "#005F73",
        "#94D2BD",
        "#3A5A40",
        "#E9C46A",
        "#6A4C93",
        "#525252",
        "#457B9D",
        "#2A9D8F",
        "#4CAF50",
        "#FFD166",
        "#8171C0",
        "#BDBDBD",
        "#1A759F",
        "#4D908E",
        "#8FB339",
        "#FFB703",
        "#4B3F72",
        "#969696",
        "#2E86AB",
        "#66C2A5",
        "#2E8B57",
        "#F2C94C",
        "#A67B5B",
        "#CCCCCC",
        "#247BA0",
        "#008080",
        "#3CB371",
        "#FFC300",
        "#BFA6A0",
        "#9EA3A7",
        "#00A8E8",
        "#66BB6A",
        "#FFB347",
        "#D2B48C",
        "#708090",
        "#0077B6",
        "#9E9D89",
        "#FFDD93",
        "#857E7D",
        "#6BAED6",
        "#1E90FF",
        "#4682B4",
        "#5F9EA0",
        "#4B0082",
        "#D7263D",
        "#F49D37",
        "#140F2D",
        "#00A896",
        "#00C49A",
        "#FF6B6B",
        "#3D5A80",
        "#98C1D9",
        "#EE6C4D",
        "#293241",
      ],
    };
  },

  beforeDestroy() {
    this.disposed = true;
    this.markers.forEach((marker) => marker.setMap(null));
    this.polylines.forEach((line) => line.setMap(null));
    if (this.markerClusterer) this.markerClusterer.clearMarkers();
    if (this.map && window.google?.maps?.event)
      window.google.maps.event.clearInstanceListeners(this.map);
    this.map = null;
  },
  computed: {
    selectedStops() {
      return this.paradas
        .filter((stop) => stop.numeroRota === this.selectedRotaNumber)
        .sort((a, b) => a.sequencia - b.sequencia);
    },
    uniqueRotaNumbers() {
      return Array.from(new Set(this.rotas.map((r) => r.numeroRota)));
    },
    totalRotas() {
      return this.uniqueRotaNumbers.length;
    },
    resumoMotivos() {
      const counts = {};
      (this.atendimentosInviaveis || []).forEach((a) => {
        // Agrupa pelo código ("JORNADA_INSUFICIENTE: ..."); o detalhe fica na lista.
        const motivo = (this.getMotivo(a) || "Motivo não informado")
          .split(":")[0]
          .trim();
        counts[motivo] = (counts[motivo] || 0) + 1;
      });
      return Object.entries(counts)
        .map(([motivo, count]) => ({ motivo, count }))
        .sort((a, b) => b.count - a.count);
    },
  },

  methods: {
    getMotivo(a) {
      // cobre diferentes nomes que podem vir do backend
      return (
        a.motivoInviabilidade ||
        a.motivoInviavel ||
        a.motivo ||
        a.motivoInviavelDescricao ||
        a.motivoInviabilidadeDescricao ||
        null
      );
    },
    handleDataChange(value) {
      this.selectedDate = value;
    },
    readFile() {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (evt) => resolve(evt.target.result);
        reader.onerror = (err) => reject(err);
        reader.readAsText(this.file, "UTF-8");
      });
    },
    async importCSV() {
      this.errors = [];
      if (!this.selectedDate) {
        this.errors.push({ message: "A data é obrigatória." });
        return;
      }
      if (!this.file) {
        this.errors.push({ message: "Selecione um arquivo CSV." });
        return;
      }
      if (!this.file.name.toLowerCase().endsWith(".csv")) {
        this.errors.push({ message: "O arquivo precisa ser .csv" });
        return;
      }

      this.loading = true;
      try {
        const contentFile = await this.readFile();
        const atendimentos = parseRoteirizacaoCsv(
          contentFile,
          this.selectedDate
        );
        const requisicaoId = window.crypto?.randomUUID
          ? window.crypto.randomUUID()
          : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
        this.progressLabel = "Preparando planejamento";
        const response = await DataService.post("roteirizador/planejamentos", {
          data: this.selectedDate,
          requisicaoId,
          atendimentos,
        });
        this.currentJobId = response.data.id;
        const result = await this.waitForPlanning(this.currentJobId);
        if (!result || this.disposed) return;
        this.applyPlanningResult(result);

        await this.loadGoogleMaps();
        this.$nextTick(() => {
          this.updateMap();
        });
      } catch (error) {
        console.error("Erro ao importar CSV:", error);
        this.errors.push({
          message:
            error.response?.data?.message ||
            error.message ||
            "Não foi possível concluir o planejamento.",
        });
      } finally {
        this.loading = false;
      }
    },
    async waitForPlanning(id) {
      const labels = {
        AGUARDANDO: "Aguardando processamento",
        PREPARANDO: "Preparando pedidos e veículos",
        RECUPERADO: "Retomando processamento",
        MATRIZ_VIARIA: "Consultando trajetos e classificando trechos",
        ANALISANDO_RESTRICOES: "Conferindo as regras dos pedidos",
        MAXIMIZANDO_ATENDIMENTO: "Buscando atender mais pedidos",
        REDUZINDO_VEICULOS: "Ajustando a quantidade de veículos",
        REDUZINDO_DISTANCIA: "Melhorando os trajetos",
        VALIDANDO: "Conferindo limites e horários",
        CANCELANDO: "Cancelando planejamento",
      };
      while (!this.disposed) {
        const response = await DataService.get(
          `roteirizador/planejamentos/${id}`
        );
        const job = response.data;
        this.progressLabel = labels[job.fase] || "Processando planejamento";
        if (job.status === "COMPLETED") {
          this.currentJobId = null;
          return job.resultado;
        }
        if (job.status === "FAILED") {
          this.currentJobId = null;
          throw new Error(job.erro || "O planejamento não pôde ser concluído.");
        }
        if (job.status === "CANCELLED") {
          this.currentJobId = null;
          return null;
        }
        await new Promise((resolve) => setTimeout(resolve, 1500));
      }
      return null;
    },
    applyPlanningResult(result) {
      this.rotas = result.rotas || [];
      this.paradas = result.paradas || [];
      this.resumoRotas = result.resumoRotas || [];
      this.atendimentosInviaveis = result.atendimentosInviaveis || [];
      this.selectedRotaNumber = null;
      this.selectedDetail = null;
      this.selectedRotas = [];
    },
    async resumePlanning() {
      if (!this.currentJobId) return;
      this.loading = true;
      try {
        const result = await this.waitForPlanning(this.currentJobId);
        if (!result || this.disposed) return;
        this.applyPlanningResult(result);
        await this.loadGoogleMaps();
        this.$nextTick(() => this.updateMap());
      } catch (error) {
        this.errors.push({
          message: error.response?.data?.message || error.message,
        });
      } finally {
        this.loading = false;
      }
    },
    async cancelPlanning() {
      if (!this.currentJobId) return;
      try {
        await DataService.post(
          `roteirizador/planejamentos/${this.currentJobId}/cancelamento`
        );
        this.progressLabel = "Cancelamento solicitado";
      } catch (error) {
        this.errors.push({
          message:
            "Não foi possível solicitar o cancelamento. Tente novamente.",
        });
      }
    },
    stopLabel(type) {
      return (
        {
          SAIDA_BASE: "Saída da base",
          RETORNO_BASE: "Retorno à base",
          COLETA: "Coleta",
          ENTREGA: "Entrega",
          ASSISTENCIA: "Assistência",
          CONCLUSAO_ASSISTENCIA: "Conclusão da assistência",
        }[type] || type
      );
    },
    formatStopTime(value) {
      return new Date(value).toLocaleString("pt-BR", {
        timeZone: "America/Sao_Paulo",
      });
    },
    selectRota(rotaNumber) {
      this.selectedRotaNumber = rotaNumber;
      this.selectedRotas = this.rotas.filter(
        (rota) => rota.numeroRota === rotaNumber
      );
      this.selectedDetail = null;
      this.updateMap();
    },
    selectDetail(rota) {
      this.selectedDetail = rota;
      this.highlightMarkers();
    },
    clearSelection() {
      this.selectedDetail = null;
      this.updateMap();
    },
    clearRouteSelection() {
      this.selectedRotaNumber = null;
      this.selectedRotas = this.rotas;
      this.selectedDetail = null;
      this.updateMap();
    },
    getCenterCoordinates() {
      for (let rota of this.rotas) {
        if (
          rota.descricaoOrigem &&
          rota.descricaoOrigem.toUpperCase().includes("CX FORTE")
        ) {
          return {
            lat: parseFloat(rota.latitudeOrigem),
            lng: parseFloat(rota.longitudeOrigem),
          };
        }
        if (
          rota.descricaoDestino &&
          rota.descricaoDestino.toUpperCase().includes("CX FORTE")
        ) {
          return {
            lat: parseFloat(rota.latitudeDestino),
            lng: parseFloat(rota.longitudeDestino),
          };
        }
      }
      return { lat: -15.793889, lng: -47.882778 };
    },
    updateMap() {
      if (this.markers.length) this.markers.forEach((m) => m.setMap(null));
      this.markers = [];
      this.polylines.forEach((line) => line.setMap(null));
      this.polylines = [];
      if (this.markerClusterer) this.markerClusterer.clearMarkers();
      if (!this.rotas.length) return;

      const center = this.getCenterCoordinates();
      if (!this.map) {
        this.map = new google.maps.Map(
          document.getElementById("map"),
          withGoogleMapsMapId({
            center,
            zoom: 10,
          })
        );
      } else {
        this.map.setCenter(center);
      }

      const rotasToProcess = this.selectedRotaNumber
        ? this.selectedRotas
        : this.rotas;
      const events = [];
      const routeCoordinates = [];
      this.paradas
        .filter(
          (stop) =>
            !this.selectedRotaNumber ||
            stop.numeroRota === this.selectedRotaNumber
        )
        .forEach((stop) => {
          const geometry = stop.trajetoProximoGeoJson;
          if (geometry?.type !== "LineString") return;
          const path = geometry.coordinates.map(([lng, lat]) => ({ lat, lng }));
          routeCoordinates.push(...path);
          this.polylines.push(
            new google.maps.Polyline({
              map: this.map,
              path,
              strokeColor: this.getLegendColor(stop.numeroRota),
              strokeOpacity: 0.8,
              strokeWeight: 4,
            })
          );
        });
      if (this.paradas.length) {
        this.paradas
          .filter(
            (stop) =>
              !this.selectedRotaNumber ||
              stop.numeroRota === this.selectedRotaNumber
          )
          .forEach((stop) => {
            events.push({
              type: stop.tipo === "COLETA" ? "origem" : "destino",
              coordinate: { lat: stop.latitude, lng: stop.longitude },
              timestamp: new Date(stop.inicioAtendimento),
              sequence: stop.sequencia,
              description: `${this.stopLabel(stop.tipo)}: ${
                stop.descricao
              }<br>Pedido: ${stop.pedido || "Base"}`,
              pedidos: stop.pedido ? [stop.pedido] : [],
              routeNumber: stop.numeroRota,
            });
          });
      } else
        rotasToProcess.forEach((rota) => {
          const timeOrigem =
            rota.janelaInicioOrigem || rota.previsaoAtendimentoOrigem;
          const timeDestino =
            rota.janelaInicioDestino || rota.previsaoAtendimentoDestino;
          if (timeOrigem && rota.latitudeOrigem && rota.longitudeOrigem) {
            const ts = new Date(`${this.selectedDate}T${timeOrigem}`);
            if (!isNaN(ts.getTime())) {
              events.push({
                type: "origem",
                coordinate: {
                  lat: parseFloat(rota.latitudeOrigem),
                  lng: parseFloat(rota.longitudeOrigem),
                },
                timestamp: ts,
                description: `Origem: ${rota.descricaoOrigem}${
                  rota.previsaoAtendimentoOrigem
                    ? " (Previsão: " + rota.previsaoAtendimentoOrigem + ")"
                    : ""
                }<br>Pedido: ${rota.pedido}`,
                pedidos: [rota.pedido],
                routeNumber: rota.numeroRota,
              });
            }
          }
          if (timeDestino && rota.latitudeDestino && rota.longitudeDestino) {
            const ts = new Date(`${this.selectedDate}T${timeDestino}`);
            if (!isNaN(ts.getTime())) {
              events.push({
                type: "destino",
                coordinate: {
                  lat: parseFloat(rota.latitudeDestino),
                  lng: parseFloat(rota.longitudeDestino),
                },
                timestamp: ts,
                description: `Destino: ${rota.descricaoDestino}${
                  rota.previsaoAtendimentoDestino
                    ? " (Previsão: " + rota.previsaoAtendimentoDestino + ")"
                    : ""
                }<br>Pedido: ${rota.pedido}`,
                pedidos: [rota.pedido],
                routeNumber: rota.numeroRota,
              });
            }
          }
        });

      const eventsMap = new Map();
      events.forEach((evt) => {
        const key = `${evt.coordinate.lat},${
          evt.coordinate.lng
        }__${evt.timestamp.getTime()}__${evt.type}__${evt.routeNumber}__${
          evt.sequence ?? "legacy"
        }`;
        if (eventsMap.has(key)) {
          const existing = eventsMap.get(key);
          existing.description += "<hr>" + evt.description;
          existing.pedidos = Array.from(
            new Set([...existing.pedidos, ...evt.pedidos])
          );
        } else {
          eventsMap.set(key, { ...evt });
        }
      });

      let uniqueEvents = Array.from(eventsMap.values()).map((e) => ({
        ...e,
        count: e.pedidos.length,
      }));
      uniqueEvents.sort(
        (a, b) =>
          a.routeNumber - b.routeNumber ||
          (a.sequence !== undefined && b.sequence !== undefined
            ? a.sequence - b.sequence
            : a.timestamp - b.timestamp)
      );

      uniqueEvents.forEach((evt, index) => {
        const timeStr = evt.timestamp.toLocaleTimeString([], {
          hour: "2-digit",
          minute: "2-digit",
        });
        const finalDescription = `<b>Ponto #${
          index + 1
        }</b><br><b>Pins neste ponto:</b> ${
          evt.count
        }<br><b>Hora:</b> ${timeStr}<br>${evt.description}`;
        const marker = createGoogleMapsMarker({
          position: evt.coordinate,
          map: this.map,
          icon: this.getMarkerIcon(evt.routeNumber, 2),
          label:
            evt.count > 1
              ? { text: String(evt.count), color: "#ffffff", fontSize: "12px" }
              : undefined,
          customPedidos: evt.pedidos,
          customType: evt.type,
          customTimestamp: evt.timestamp,
          routeNumber: evt.routeNumber,
          customCount: evt.count,
        });
        marker.originalContent = DOMPurify.sanitize(finalDescription);
        const infoWindow = new google.maps.InfoWindow({
          content: DOMPurify.sanitize(finalDescription),
        });
        marker.infoWindow = infoWindow;
        marker.addListener("click", () =>
          openGoogleMapsInfoWindow(infoWindow, this.map, marker)
        );
        marker.addListener("mouseover", () =>
          openGoogleMapsInfoWindow(infoWindow, this.map, marker)
        );
        marker.addListener("mouseout", () => infoWindow.close());
        this.markers.push(marker);
      });

      this.markerClusterer = new MarkerClusterer({
        map: this.map,
        markers: this.markers,
      });
      if (this.markers.length) {
        let bounds = new google.maps.LatLngBounds();
        this.markers.forEach((m) => bounds.extend(m.getPosition()));
        routeCoordinates.forEach((point) => bounds.extend(point));
        this.map.fitBounds(bounds);
      }
      if (this.selectedDetail) {
        this.highlightMarkers();
      }
    },
    highlightMarkers() {
      if (!this.selectedDetail) return;
      const selectedOrigemColor = "#1E90FF";
      const selectedDestinoColor = "#32CD32";
      const selectedScale = 3;

      this.markers.forEach((marker) => {
        if (
          marker.customPedidos &&
          marker.customPedidos.includes(this.selectedDetail.pedido)
        ) {
          if (marker.customType === "origem") {
            marker.setIcon(
              this.getSvgMarkerIcon(selectedOrigemColor, selectedScale, true)
            );
          } else if (marker.customType === "destino") {
            marker.setIcon(
              this.getSvgMarkerIcon(selectedDestinoColor, selectedScale, true)
            );
          } else {
            marker.setIcon(
              this.getSvgMarkerIcon(
                this.colorPalette[
                  (marker.routeNumber - 1) % this.colorPalette.length
                ],
                selectedScale,
                true
              )
            );
          }
          marker.setAnimation(google.maps.Animation.BOUNCE);
          const timeStr = marker.customTimestamp
            ? marker.customTimestamp.toLocaleTimeString([], {
                hour: "2-digit",
                minute: "2-digit",
              })
            : "";
          const newContent =
            marker.originalContent +
            `<br><b>Horário Previsto:</b> ${timeStr}<br>Tipo: ${
              marker.customType === "origem" ? "Origem" : "Destino"
            }`;
          marker.infoWindow.setContent(newContent);
        } else {
          marker.setIcon(this.getMarkerIcon(marker.routeNumber, 2));
          marker.setAnimation(null);
          marker.infoWindow.close();
        }
        marker.setLabel(
          marker.customCount > 1
            ? {
                text: String(marker.customCount),
                color: "#ffffff",
                fontSize: "12px",
              }
            : null
        );
      });
    },
    getSvgMarkerIcon(color, scale = 2, asObject = false) {
      const width = Math.round(32 * scale);
      const height = Math.round(48 * scale);
      const svg = `<svg xmlns='http://www.w3.org/2000/svg' width='${width}' height='${height}' viewBox='0 0 32 48'><path fill='${color}' stroke='white' stroke-width='2' d='M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z'/></svg>`;
      const dataUrl =
        "data:image/svg+xml;charset=UTF-8," + encodeURIComponent(svg);
      if (asObject) {
        return {
          url: dataUrl,
          scaledSize: new google.maps.Size(width, height),
          anchor: new google.maps.Point(width / 2, height),
          labelOrigin: new google.maps.Point(width / 2, height / 2),
        };
      }
      return dataUrl;
    },
    getMarkerIcon(rotaNumber, scale = 2) {
      const color =
        this.colorPalette[
          (parseInt(rotaNumber) - 1) % this.colorPalette.length
        ];
      return this.getSvgMarkerIcon(color, scale, true);
    },
    getLegendColor(rotaNumber) {
      const index = (parseInt(rotaNumber) - 1) % this.colorPalette.length;
      return this.colorPalette[index];
    },
    getRouteType(rotaNumber) {
      const summary = this.resumoRotas.find(
        (item) => item.numeroRota === rotaNumber
      );
      return summary?.tipoPercurso === "INTERURBANO" ? "Interurbano" : "Urbano";
    },
    getPlaca(rotaNumber) {
      const rota = this.rotas.find((r) => r.numeroRota === rotaNumber);
      return rota && rota.placaVeiculo ? rota.placaVeiculo : "";
    },
    exportToExcel() {
      if (!this.rotas.length && !this.atendimentosInviaveis.length) return;
      const workbook = XLSX.utils.book_new();
      if (this.paradas.length)
        XLSX.utils.book_append_sheet(
          workbook,
          XLSX.utils.json_to_sheet(this.paradas),
          "Paradas"
        );
      if (this.resumoRotas.length)
        XLSX.utils.book_append_sheet(
          workbook,
          XLSX.utils.json_to_sheet(this.resumoRotas),
          "Resumo"
        );

      if (this.rotas.length) {
        const wsRotas = XLSX.utils.json_to_sheet(this.rotas);
        XLSX.utils.book_append_sheet(workbook, wsRotas, "Rotas");
      }

      if (this.atendimentosInviaveis.length) {
        const inviaveisComMotivo = this.atendimentosInviaveis.map((a) => ({
          ...a,
          motivo: this.getMotivo(a) || "Motivo não informado",
        }));
        const wsInv = XLSX.utils.json_to_sheet(inviaveisComMotivo);
        XLSX.utils.book_append_sheet(workbook, wsInv, "Inviaveis");
      }

      const excelBuffer = XLSX.write(workbook, {
        bookType: "xlsx",
        type: "array",
      });
      const data = new Blob([excelBuffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8",
      });
      const url = window.URL.createObjectURL(data);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "roteirizacao.xlsx");
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    },
    formatCurrency(value) {
      return new Intl.NumberFormat("pt-BR", {
        style: "currency",
        currency: "BRL",
      }).format(value || 0);
    },
    loadGoogleMaps() {
      const waitForGoogleMaps = () =>
        new Promise((resolve, reject) => {
          const startedAt = Date.now();
          const timer = setInterval(() => {
            if (window.google && window.google.maps) {
              clearInterval(timer);
              resolve(window.google.maps);
              return;
            }
            if (Date.now() - startedAt > 15000) {
              clearInterval(timer);
              reject(new Error("Google Maps indisponível"));
            }
          }, 100);
        });

      return new Promise((resolve, reject) => {
        if (window.google && window.google.maps) {
          return resolve(window.google.maps);
        }
        const existingScript = document.querySelector(
          "script[data-google-maps-api], script[src*='maps.googleapis.com/maps/api/js']"
        );
        if (existingScript) {
          waitForGoogleMaps().then(resolve).catch(reject);
          return;
        }
        const script = document.createElement("script");
        script.src =
          "https://maps.googleapis.com/maps/api/js?key=AIzaSyDZSVtVJNqlO3RJ96MNiJKc3-C5CPx_WGM&loading=async&libraries=marker";
        script.async = true;
        script.defer = true;
        script.dataset.googleMapsApi = "true";
        script.onload = () => waitForGoogleMaps().then(resolve).catch(reject);
        script.onerror = (err) => reject(err);
        document.head.appendChild(script);
      });
    },
  },
  mounted() {
    this.loadGoogleMaps().catch((error) => {
      console.error("Erro ao carregar a API do Google Maps:", error);
      this.errors.push({ message: "Erro ao carregar a API do Google Maps." });
    });
  },
};
</script>

<style scoped>
.selected-route {
  background-color: #eef1ff !important;
}

.plan-panel {
  background: #fff;
  border: 1px solid #e6e9ef;
  border-radius: 12px;
  box-shadow: 0 5px 18px rgba(33, 43, 54, 0.05);
  overflow: hidden;
}

.plan-panel__header {
  align-items: center;
  background: #fbfcfe;
  border-bottom: 1px solid #edf0f4;
  display: flex;
  gap: 8px;
  justify-content: space-between;
  min-height: 48px;
  padding: 8px 14px;
}

.plan-panel__header--wrap {
  flex-wrap: wrap;
}

.plan-panel__title {
  align-items: center;
  color: #344054;
  display: flex;
  font-size: 0.78rem;
  font-weight: 800;
  letter-spacing: 0.025em;
  text-transform: uppercase;
}

.plan-panel__body {
  max-height: 640px;
  overflow-y: auto;
}

.plan-panel__body--unallocated {
  max-height: 360px;
}

.plan-panel__footnote {
  border-top: 1px solid #edf0f4;
  color: #667085;
  font-size: 0.72rem;
  padding: 6px 14px;
}

.plan-map {
  height: 640px;
}

.plan-route {
  align-items: center;
  background: #fff;
  border: 0;
  border-bottom: 1px solid #f2f4f7;
  display: flex;
  gap: 10px;
  padding: 9px 14px;
  text-align: left;
  width: 100%;
}

.plan-route:hover {
  background: #f9faff;
}

.plan-route:focus-visible,
.plan-stop--button:focus-visible {
  outline: 3px solid #8da8ff;
  outline-offset: -3px;
}

.plan-route__color {
  border-radius: 4px;
  flex: 0 0 14px;
  height: 14px;
  width: 14px;
}

.plan-route__text {
  display: flex;
  flex: 1;
  flex-direction: column;
  min-width: 0;
}

.plan-route__text strong {
  color: #202538;
  font-size: 0.82rem;
}

.plan-route__text small {
  color: #667085;
  font-size: 0.72rem;
}

.plan-stop {
  border-bottom: 1px solid #f2f4f7;
  display: flex;
  gap: 10px;
  padding: 10px 14px;
}

.plan-stop--button {
  background: #fff;
  border-left: 0;
  border-right: 0;
  border-top: 0;
  text-align: left;
  width: 100%;
}

.plan-stop--button:hover {
  background: #f9faff;
}

.plan-stop__sequence {
  align-items: center;
  border: 2px solid;
  border-radius: 50%;
  color: #344054;
  display: inline-flex;
  flex: 0 0 26px;
  font-size: 0.72rem;
  font-weight: 800;
  height: 26px;
  justify-content: center;
}

.plan-stop__content {
  color: #667085;
  display: flex;
  flex-direction: column;
  font-size: 0.75rem;
  gap: 1px;
  min-width: 0;
}

.plan-stop__content strong {
  color: #202538;
  font-size: 0.8rem;
}

.plan-stop__type {
  color: #3e4fb8;
  font-weight: 700;
}

.plan-stop__warning {
  color: #b54708;
  font-weight: 700;
}

.plan-reason {
  background: #fef3f2;
  border: 1px solid #fecdca;
  border-radius: 999px;
  color: #b42318;
  font-size: 0.7rem;
  font-weight: 700;
  margin: 2px;
  padding: 2px 9px;
}
</style>
